/**
 * client/src/lib/use-recovery-options.ts
 *
 * The single client read of the server's recovery-preparation facts (AC-52).
 * `GET /api/security/recovery-options` already decides which methods are
 * offerable — including the rule that trusted people cannot be the only route
 * — and reports `recoveryOfferable` alongside them.
 *
 * Every consumer (the Security section nav, the Recovery page, the Security
 * Centre next step) reads THIS hook, so they share one TanStack cache entry
 * and therefore one snapshot. Two independent fetches of the same endpoint
 * could disagree, and a nav that hides a tab whose page is showing methods —
 * or offers one whose page is empty — is exactly the dead route the AC forbids.
 *
 * Fails closed: an unloaded, failed, or unparseable response offers nothing.
 */

import { useQuery } from "@tanstack/react-query";

import {
  recoveryOptionsResponseSchema,
  type RecoveryMethodKey,
  type RecoveryOptionsResponse,
} from "@shared/security-recovery-options";

import { apiRequest } from "./queryClient";

export const RECOVERY_OPTIONS_QUERY_KEY = ["security/recovery-options"] as const;

export interface RecoveryOfferings {
  /** Whether the query has resolved into a valid response. */
  loaded: boolean;
  /** The server's own answer to "is there anything to prepare?". */
  offerable: boolean;
  /** Keys of the methods the server would render, in server order. */
  methodKeys: readonly RecoveryMethodKey[];
  /** The parsed response, for surfaces that render the methods themselves. */
  options: RecoveryOptionsResponse | undefined;
  /** The fetch failed, or the response did not match the wire contract. */
  failed: boolean;
  /** Refetch after a failure — the "Try again" affordance. */
  reload: () => void;
}

async function fetchRecoveryOptions(): Promise<RecoveryOptionsResponse> {
  const response = await apiRequest("/api/security/recovery-options");
  const parsed = recoveryOptionsResponseSchema.safeParse(response);
  if (!parsed.success) {
    // Same bounded, data-free convention as reportSecurityCentreDiagnostic —
    // inlined rather than imported, because client/src/lib must not depend on
    // a page module. A wire-shape regression degrades every recovery surface
    // to "nothing offerable", so it must not be silent.
    console.error("security_centre.recovery_options_unparseable");
    throw new Error("recovery options response did not match the wire contract");
  }
  return parsed.data;
}

/**
 * Server-owned recovery-preparation facts, shared across the security surfaces.
 *
 * Deliberately NOT gated on `useCurrentUser`: both mount sites live inside the
 * authenticated shell, the route is `requireAuth`, and `retry: false` bounds an
 * unauthenticated call to a single 401 — while gating would make the Recovery
 * page wait on a second query it never needed before.
 */
export function useRecoveryOfferings(): RecoveryOfferings {
  const { data, isError, refetch } = useQuery({
    queryKey: RECOVERY_OPTIONS_QUERY_KEY,
    queryFn: fetchRecoveryOptions,
    retry: false,
    staleTime: 30_000,
  });

  return {
    loaded: data !== undefined,
    offerable: data?.recoveryOfferable ?? false,
    methodKeys: data?.methods.map((method) => method.key) ?? [],
    options: data,
    failed: isError,
    reload: () => { void refetch(); },
  };
}
